<template>
	<v-row class="full-height payment-method-page">
		<v-col cols="12" class="flex-none pb-0">
			<v-card class="payment-method-toolbar-card" flat>
				<v-card-text class="pa-3">
					<v-row align="center" dense>
						<!-- Search -->
						<v-col cols="12" sm="6" md="4" lg="3">
							<v-text-field
								v-model="searchKeywords"
								placeholder="Search payment methods..."
								prepend-inner-icon="mdi-magnify"
								outlined
								dense
								hide-details
								clearable
								class="payment-method-search"
								@input="onSearchInput"
								@click:clear="onSearchClear"
							></v-text-field>
						</v-col>

						<!-- Spacer -->
						<v-spacer class="d-none d-md-flex"></v-spacer>

						<!-- Delete Button (visible when items selected) -->
						<v-col cols="auto" v-if="deleteMethods.length > 0" class="mr-2">
							<v-btn
								depressed
								rounded
								color="error"
								@click="deleteWarning = true"
							>
								<v-icon left>mdi-delete</v-icon>
								Delete ({{ deleteMethods.length }})
							</v-btn>
						</v-col>

						<!-- New Pay Method Button -->
						<v-col cols="auto">
							<v-btn
								depressed
								rounded
								color="success"
								@click="newPaymentMethod()"
								:disabled="activeEdit !== null"
							>
								New Pay Method
							</v-btn>
						</v-col>
					</v-row>
				</v-card-text>
			</v-card>
		</v-col>

		<v-col cols="12" class="flex-auto pt-0 payment-method-table-container">
			<v-data-table
				:headers="headers"
				:items="displayPaymentMethods"
				:loading="$apollo.queries.paymentMethods.loading"
				:options.sync="tableOptions"
				:server-items-length="totalCount"
				:footer-props="footerProps"
				:mobile-breakpoint="0"
				class="payment-method-data-table"
				fixed-header
				show-select
				v-model="deleteMethods"
				:item-key="'paymentMethodId'"
				:disable-sort="activeEdit !== null"
			>
				<!-- Row attributes for drag and drop -->
				<template v-slot:body="{ items }">
					<tbody
						@drop="onDrop($event)"
						@dragover.prevent="onDragOver($event)"
						@dragenter.prevent
					>
						<tr
							v-for="item in items"
							:key="item.paymentMethodId || 'new'"
							:data-id="item.paymentMethodId"
							:draggable="activeEdit !== null"
							:class="{ dragging: dragItem === item, 'drag-over': dragOverItem === item }"
							@dragstart="onDragStart($event, item)"
							@dragend="onDragEnd"
						>
							<td>
								<v-simple-checkbox
									v-if="item.paymentMethodId !== null"
									:value="deleteMethods.includes(item)"
									@input="toggleSelection(item, $event)"
									color="accent"
									:disabled="activeEdit !== null"
								></v-simple-checkbox>
							</td>
							<td>
								<template v-if="item === activeEdit">
									<v-text-field
										v-model="item.paymentMethodName"
										outlined
										dense
										hide-details="auto"
										@keyup.enter="savePaymentMethod()"
										@keyup.esc="cancelEdit(item)"
										autofocus
									></v-text-field>
								</template>
								<template v-else>
									{{ item.paymentMethodName }}
								</template>
							</td>
							<td class="text-center sequence-cell">
								<v-icon
									v-if="activeEdit !== null"
									class="drag-handle"
									style="font-size: 2rem; cursor: grab"
								>
									mdi-drag-vertical
								</v-icon>
								<span v-else>{{ item.sequence }}</span>
							</td>
							<td class="text-end">
								<template v-if="item === activeEdit">
									<v-btn
										icon
										color="error"
										@click="cancelEdit(item)"
										title="Cancel"
									>
										<v-icon style="font-size: 2rem">mdi-close</v-icon>
									</v-btn>
									<v-btn
										icon
										color="success"
										@click="savePaymentMethod()"
										title="Save"
									>
										<v-icon style="font-size: 2rem">mdi-check</v-icon>
									</v-btn>
								</template>
								<template v-else>
									<v-btn
										icon
										color="primary"
										@click="activeEdit = item"
										:disabled="activeEdit !== null"
										title="Edit"
									>
										<v-icon style="font-size: 2rem">mdi-pencil</v-icon>
									</v-btn>
								</template>
							</td>
						</tr>
					</tbody>
				</template>

				<!-- Loading slot -->
				<template v-slot:loading>
					<v-skeleton-loader type="table-row@10"></v-skeleton-loader>
				</template>

				<!-- No data slot -->
				<template v-slot:no-data>
					<div class="text-center pa-4">
						<v-icon large color="grey lighten-1">mdi-cash-multiple</v-icon>
						<p class="mt-2 grey--text">No payment methods found</p>
					</div>
				</template>
			</v-data-table>
		</v-col>

		<WarningModal
			v-if="deleteWarning"
			:showModal="deleteWarning"
			:message="'Are you sure you want to delete the selected payment methods?'"
			:itemList="deleteMethods"
			:itemListKey="'paymentMethodName'"
			:cancelText="'No, Cancel'"
			:confirmText="'Yes, Delete'"
			@cancel="deleteWarning = false"
			@confirm="
				deletePaymentMethod();
				deleteWarning = false;
			"
		/>
	</v-row>
</template>

<script>
import gql from "graphql-tag";
import WarningModal from "@/components/WarningModal.vue";
import { getApiUrl } from "@/helpers";

export default {
	name: "PaymentMethod",
	components: {
		WarningModal,
	},
	data() {
		return {
			searchKeywords: "",
			searchTimeout: null,
			paymentMethods: [],
			deleteMethods: [],
			activeEdit: null,
			deleteWarning: false,
			totalCount: 0,
			dragItem: null,
			dragOverItem: null,
			originalSequences: null,
			tableOptions: {
				page: 1,
				itemsPerPage: 15,
				sortBy: ["sequence"],
				sortDesc: [false],
			},
			footerProps: {
				"items-per-page-options": [10, 15, 25, 50],
				"show-first-last-page": true,
			},
			headers: [
				{
					text: "Description",
					value: "paymentMethodName",
					sortable: true,
					width: "70%",
				},
				{
					text: "Sequence",
					value: "sequence",
					sortable: true,
					width: "15%",
					align: "center",
				},
				{
					text: "",
					value: "action",
					sortable: false,
					width: "15%",
					align: "end",
				},
			],
		};
	},
	apollo: {
		paymentMethods: {
			query: gql`
				query GetPaymentMethods($pageOffset: Int!, $take: Int!, $order: [PaymentMethodSortInput!], $where: PaymentMethodFilterInput) {
					paymentMethods(skip: $pageOffset, take: $take, order: $order, where: $where) {
						items {
							paymentMethodId
							paymentMethodName
							sequence
						}
						totalCount
					}
				}
			`,
			variables() {
				const { page, itemsPerPage, sortBy, sortDesc } = this.tableOptions;

				// Build dynamic sort order
				const sortColumn = sortBy[0] || "sequence";
				const sortDirection = sortDesc[0] ? "DESC" : "ASC";
				const sortFieldName = this.getSortFieldName(sortColumn);
				const order = [{ [sortFieldName]: sortDirection }];

				// Build where clause
				let where = null;
				if (this.searchKeywords?.trim()) {
					where = {
						paymentMethodName: { contains: this.searchKeywords.trim() },
					};
				}

				return {
					pageOffset: (page - 1) * itemsPerPage,
					take: itemsPerPage,
					order,
					where,
				};
			},
			update(data) {
				this.totalCount = data.paymentMethods.totalCount;
				return data.paymentMethods.items;
			},
			notifyOnNetworkStatusChange: true,
			debounce: 400,
		},
	},
	computed: {
		displayPaymentMethods() {
			// If editing a new item, include it at the top
			if (this.activeEdit && !this.activeEdit.paymentMethodId) {
				return [this.activeEdit, ...this.paymentMethods];
			}
			return this.paymentMethods;
		},
	},
	methods: {
		toggleSelection(item, isSelected) {
			if (isSelected) {
				if (!this.deleteMethods.includes(item)) {
					this.deleteMethods.push(item);
				}
			} else {
				this.deleteMethods = this.deleteMethods.filter(m => m !== item);
			}
		},

		// Map table column values to GraphQL sort field names
		getSortFieldName(columnValue) {
			const sortFieldMap = {
				paymentMethodName: "paymentMethodName",
				sequence: "sequence",
			};
			return sortFieldMap[columnValue] || "sequence";
		},

		newPaymentMethod() {
			const newPayment = {
				paymentMethodId: null,
				paymentMethodName: "",
				sequence: this.paymentMethods.length > 0 ? Math.max(...this.paymentMethods.map(m => m.sequence)) + 1 : 1,
			};

			this.activeEdit = newPayment;
		},

		cancelEdit(item) {
			if (item.paymentMethodId === null) {
				// Remove new unsaved item - it's only in displayPaymentMethods, not paymentMethods
			}
			this.activeEdit = null;
			this.originalSequences = null;
			this.$apollo.queries.paymentMethods.refetch();
		},

		async deletePaymentMethod() {
			try {
				for (const method of this.deleteMethods) {
					await this.$http.delete(
						getApiUrl(`/api/settings/payment-method/${method.paymentMethodId}`)
					);
				}

				this.deleteMethods = [];
				this.$apollo.queries.paymentMethods.refetch();
			} catch (error) {
				console.error("Error deleting payment methods:", error);
			}
		},

		async savePaymentMethod() {
			try {
				if (!this.activeEdit.paymentMethodName || this.activeEdit.paymentMethodName.trim() === "") {
					alert("Payment method name is required");
					return;
				}

				// Save the active edit item first
				console.log('[PaymentMethod] Saving activeEdit:', this.activeEdit);
				if (this.activeEdit.paymentMethodId) {
					await this.$http.put(
						getApiUrl("/api/settings/payment-method"),
						this.activeEdit
					);
				} else {
					await this.$http.post(
						getApiUrl("/api/settings/payment-method"),
						this.activeEdit
					);
				}

				// Save all other items that have changed sequences (from drag-and-drop)
				console.log('[PaymentMethod] originalSequences:', this.originalSequences);

				if (this.originalSequences) {
					for (const item of this.paymentMethods) {
						if (item.paymentMethodId &&
							item !== this.activeEdit &&
							this.originalSequences[item.paymentMethodId] !== item.sequence) {
							console.log('[PaymentMethod] Saving sequence change for:', item.paymentMethodId, 'from', this.originalSequences[item.paymentMethodId], 'to', item.sequence);
							await this.$http.put(
								getApiUrl("/api/settings/payment-method"),
								item
							);
						}
					}
					this.originalSequences = null;
				}

				this.activeEdit = null;
				this.$apollo.queries.paymentMethods.refetch();
			} catch (error) {
				console.error("Error saving payment method:", error);
			}
		},

		onSearchInput() {
			if (this.searchTimeout) {
				clearTimeout(this.searchTimeout);
			}

			this.searchTimeout = setTimeout(() => {
				this.tableOptions.page = 1;
			}, 400);
		},

		onSearchClear() {
			this.searchKeywords = "";
			this.tableOptions.page = 1;
		},

		// Drag and drop sorting functions
		onDragStart(event, item) {
			console.log('[PaymentMethod] onDragStart - item:', item.paymentMethodId, item.paymentMethodName);

			// Capture original sequences before drag-and-drop starts
			if (!this.originalSequences) {
				this.originalSequences = {};
				this.paymentMethods.forEach(method => {
					if (method.paymentMethodId) {
						this.originalSequences[method.paymentMethodId] = method.sequence;
					}
				});
				console.log('[PaymentMethod] Captured originalSequences:', { ...this.originalSequences });
			}

			this.dragItem = item;
			event.dataTransfer.effectAllowed = 'move';
			event.dataTransfer.setData('text/plain', item.paymentMethodId);

			// Add visual feedback
			setTimeout(() => {
				event.target.classList.add('dragging');
			}, 0);
		},

		onDragEnd(event) {
			console.log('[PaymentMethod] onDragEnd');
			event.target.classList.remove('dragging');
			this.dragItem = null;
			this.dragOverItem = null;
		},

		onDragOver(event) {
			event.preventDefault();
			const targetRow = event.target.closest('tr');
			if (!targetRow || !this.dragItem) return;

			const targetId = parseInt(targetRow.dataset.id);
			const targetItem = this.paymentMethods.find(m => m.paymentMethodId === targetId);

			if (targetItem && targetItem !== this.dragItem) {
				this.dragOverItem = targetItem;

				// Get the position to insert
				const dragIndex = this.paymentMethods.findIndex(m => m === this.dragItem);
				const targetIndex = this.paymentMethods.findIndex(m => m === targetItem);

				if (dragIndex !== -1 && targetIndex !== -1 && dragIndex !== targetIndex) {
					console.log('[PaymentMethod] Reordering: moving', this.dragItem.paymentMethodId, 'from index', dragIndex, 'to', targetIndex);

					// Remove the dragged item from its current position
					const [removed] = this.paymentMethods.splice(dragIndex, 1);
					// Insert at the new position
					this.paymentMethods.splice(targetIndex, 0, removed);

					// Update sequences
					this.paymentMethods.forEach((method, index) => {
						method.sequence = index + 1;
					});

					console.log('[PaymentMethod] New order:', this.paymentMethods.map(m => ({ id: m.paymentMethodId, seq: m.sequence })));
				}
			}
		},

		onDrop(event) {
			event.preventDefault();
			console.log('[PaymentMethod] onDrop - final sequences:', this.paymentMethods.map(m => ({ id: m.paymentMethodId, seq: m.sequence })));
			this.dragOverItem = null;
		},
	},
	beforeDestroy() {
		if (this.searchTimeout) {
			clearTimeout(this.searchTimeout);
		}
	},
};
</script>

<style lang="scss" scoped>
.payment-method-page {
	height: calc(100vh - 88px);
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.payment-method-table-container {
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
	overflow: hidden;
}

.payment-method-toolbar-card {
	border-radius: 4px 4px 0 0 !important;
	border-bottom: 1px solid $color-gray;
	flex-shrink: 0;
}

.payment-method-search {
	::v-deep .v-input__slot {
		background-color: $color-gray-light-2 !important;
	}
}

.payment-method-data-table {
	border-radius: 0 0 4px 4px !important;
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
	overflow: hidden;

	::v-deep .v-data-table__wrapper {
		flex: 1;
		overflow-y: auto;
		overflow-x: auto;

		table {
			min-width: 600px;
		}

		tbody tr {
			transition: background-color 0.2s;

			&.dragging {
				opacity: 0.4;
				background-color: $color-gray-light !important;
			}

			&.drag-over {
				border-top: 2px solid $color-tertiary;
			}

			&[draggable="true"] {
				cursor: grab;

				&:active {
					cursor: grabbing;
				}
			}
		}

		.drag-handle {
			color: $color-gray-dark;

			&:hover {
				color: $color-tertiary;
			}
		}
	}

	::v-deep .v-data-footer {
		flex-shrink: 0;
	}

	::v-deep > .v-data-table__wrapper > table > thead > tr > th {
		font-size: 1.4rem !important;
		font-weight: 500 !important;
		color: #000 !important;
		text-transform: uppercase;
		background-color: $color-gray-light-2 !important;
		white-space: nowrap;
	}

	::v-deep > .v-data-table__wrapper > table > tbody > tr > td {
		height: 60px !important;
		font-size: 1.6rem !important;
	}

	::v-deep .v-data-footer {
		font-size: 1.25rem !important;
	}

	::v-deep .v-data-footer__select .v-select__selections .v-select__selection--comma {
		font-size: 1.15rem !important;
	}
}
</style>
